/**
 * CustomerSubscriptionsPage - Admin page to manage customer subscriptions
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  RefreshCw, Search, XCircle, PlayCircle, Plus, RotateCcw, Download, 
  Users, CreditCard, TrendingUp, AlertCircle, CheckCircle,
  ChevronDown, ChevronUp, MoreHorizontal
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerSubscriptionsPage = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Dialog states
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [addCreditsDialogOpen, setAddCreditsDialogOpen] = useState(false);
  const [renewDialogOpen, setRenewDialogOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [creditsToAdd, setCreditsToAdd] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const handler = setTimeout(() => {
      loadSubscriptions(searchTerm);
    }, 300);

    return () => clearTimeout(handler);
  }, [statusFilter, searchTerm]);

  const loadSubscriptions = async (searchValue = searchTerm) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = { status: statusFilter };
      if (searchValue) {
        params.search = searchValue;
      }
      const response = await axios.get(`${API}/subscriptions/admin/customer-subscriptions`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptions(response.data.subscriptions || []);
      setStatistics(response.data.statistics || {});
    } catch (error) {
      console.error('Load subscriptions error:', error);
      toast.error('Fehler beim Laden der Abonnements');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!selectedSubscription) return;
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/subscriptions/admin/customer-subscriptions/${selectedSubscription.id}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Abo "${selectedSubscription.name}" wurde gekündigt`);
      setCancelDialogOpen(false);
      setSelectedSubscription(null);
      loadSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Kündigen');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async (sub) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/subscriptions/admin/customer-subscriptions/${sub.id}/reactivate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Abo "${sub.name}" wurde reaktiviert`);
      loadSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Reaktivieren');
    } finally {
      setActionLoading(false);
    }
  };

  const handleActivate = async (sub) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/subscriptions/admin/customer-subscriptions/${sub.id}/activate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Abo "${sub.name}" wurde aktiviert`);
      loadSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktivieren');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddCredits = async () => {
    if (!selectedSubscription || creditsToAdd <= 0) return;
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/subscriptions/admin/customer-subscriptions/${selectedSubscription.id}/add-credits`,
        {},
        { 
          params: { credits: creditsToAdd },
          headers: { Authorization: `Bearer ${token}` } 
        }
      );
      toast.success(`${creditsToAdd} Credits hinzugefügt`);
      setAddCreditsDialogOpen(false);
      setSelectedSubscription(null);
      setCreditsToAdd(0);
      loadSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hinzufügen');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRenew = async () => {
    if (!selectedSubscription) return;
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/subscriptions/admin/customer-subscriptions/${selectedSubscription.id}/renew`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Abo verlängert - ${response.data.credits_added} Credits hinzugefügt. Rechnung: ${response.data.invoice_number}`);
      setRenewDialogOpen(false);
      setSelectedSubscription(null);
      loadSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Verlängerung');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return `CHF ${(amount || 0).toFixed(2)}`;
  };

  // Filter subscriptions based on search
  const filteredSubscriptions = subscriptions.filter(sub => {
    const searchLower = searchTerm.toLowerCase();
    return (
      sub.name?.toLowerCase().includes(searchLower) ||
      sub.customer_name?.toLowerCase().includes(searchLower) ||
      sub.customer_email?.toLowerCase().includes(searchLower) ||
      sub.service_name?.toLowerCase().includes(searchLower)
    );
  });

  const getStatusBadge = (status) => {
    const variants = {
      active: { className: 'bg-green-100 text-green-800', label: 'Aktiv' },
      exhausted: { className: 'bg-orange-100 text-orange-800', label: 'Aufgebraucht' },
      cancelled: { className: 'bg-red-100 text-red-800', label: 'Gekündigt' },
      pending: { className: 'bg-yellow-100 text-yellow-800', label: 'Ausstehend' },
      expired: { className: 'bg-gray-100 text-gray-800', label: 'Abgelaufen' }
    };
    const variant = variants[status] || { className: 'bg-gray-100 text-gray-800', label: status };
    return <Badge className={variant.className}>{variant.label}</Badge>;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Kunden-Abonnements</h1>
          <p className="text-gray-500 mt-1">Verwalte alle aktiven Kundenabos</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            data-testid="backfill-point-sales-btn"
            onClick={async () => {
              try {
                const token = localStorage.getItem('token');
                const { data } = await axios.post(
                  `${API}/subscriptions/admin/customer-subscriptions/backfill-point-sales`,
                  {},
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success(`${data.created} Alt-Verkäufe importiert (${data.skipped} bereits vorhanden/übersprungen)`);
                loadSubscriptions();
              } catch {
                toast.error('Import fehlgeschlagen');
              }
            }}
          >
            <Download className="w-4 h-4 mr-2" />
            Alt-Verkäufe importieren
          </Button>
          <Button onClick={loadSubscriptions} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Aktualisieren
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Gesamt</p>
                <p className="text-2xl font-bold">{statistics.total || 0}</p>
              </div>
              <Users className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Aktiv</p>
                <p className="text-2xl font-bold text-green-600">{statistics.active || 0}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Wiederkehrend</p>
                <p className="text-2xl font-bold text-purple-600">{statistics.recurring || 0}</p>
              </div>
              <RefreshCw className="h-8 w-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Umsatz</p>
                <p className="text-2xl font-bold text-yellow-600">{formatCurrency(statistics.revenue)}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-yellow-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Suche nach Name, Kunde, E-Mail..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Status</SelectItem>
                <SelectItem value="active">Aktiv</SelectItem>
                <SelectItem value="exhausted">Aufgebraucht</SelectItem>
                <SelectItem value="cancelled">Gekündigt</SelectItem>
                <SelectItem value="pending">Ausstehend</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Subscriptions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Abonnements ({filteredSubscriptions.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : filteredSubscriptions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CreditCard className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>Keine Abonnements gefunden</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Kunde</TableHead>
                    <TableHead>Abo</TableHead>
                    <TableHead>Credits</TableHead>
                    <TableHead>Preis</TableHead>
                    <TableHead>Typ</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Erstellt</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSubscriptions.map((sub) => (
                    <TableRow key={sub.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{sub.customer_name}</p>
                          <p className="text-xs text-gray-500">{sub.customer_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{sub.name}</p>
                          {sub.service_name && (
                            <p className="text-xs text-gray-500">{sub.service_name}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{sub.credits_remaining}</span>
                            <span className="text-gray-400">/ {sub.credits_total} {(sub.name || '').toLowerCase().includes('punkte') ? 'Punkte' : 'Trainings'}</span>
                          </div>
                          <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${
                                sub.credits_remaining > 0 ? 'bg-green-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${100 - sub.usage_percent}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-500">{sub.usage_percent}% verwendet</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{formatCurrency(sub.price)}</p>
                          {sub.discount_percent > 0 && (
                            <p className="text-xs text-green-600">-{sub.discount_percent}% Rabatt</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {sub.is_recurring ? (
                            <Badge variant="outline" className="text-purple-600 border-purple-600">
                              <RefreshCw className="w-3 h-3 mr-1" />
                              Wiederkehrend
                            </Badge>
                          ) : (
                            <Badge variant="outline">Einmalig</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(sub.status)}</TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {formatDate(sub.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {/* Aktivieren - nur für pending/inactive Abos */}
                            {(sub.status === 'pending' || sub.status === 'inactive') && (
                              <DropdownMenuItem onClick={() => handleActivate(sub)}>
                                <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                                Aktivieren
                              </DropdownMenuItem>
                            )}
                            {/* Reaktivieren - nur für cancelled Abos */}
                            {sub.status === 'cancelled' ? (
                              <DropdownMenuItem onClick={() => handleReactivate(sub)}>
                                <PlayCircle className="w-4 h-4 mr-2 text-green-600" />
                                Reaktivieren
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem 
                                onClick={() => {
                                  setSelectedSubscription(sub);
                                  setCancelDialogOpen(true);
                                }}
                              >
                                <XCircle className="w-4 h-4 mr-2 text-red-600" />
                                Kündigen
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem 
                              onClick={() => {
                                setSelectedSubscription(sub);
                                setAddCreditsDialogOpen(true);
                              }}
                            >
                              <Plus className="w-4 h-4 mr-2 text-blue-600" />
                              Credits hinzufügen
                            </DropdownMenuItem>
                            {sub.is_recurring && (
                              <DropdownMenuItem 
                                onClick={() => {
                                  setSelectedSubscription(sub);
                                  setRenewDialogOpen(true);
                                }}
                              >
                                <RotateCcw className="w-4 h-4 mr-2 text-purple-600" />
                                Manuell verlängern
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Abo kündigen</DialogTitle>
            <DialogDescription>
              Möchtest du das Abo "{selectedSubscription?.name}" für {selectedSubscription?.customer_name} wirklich kündigen?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-yellow-600 shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium">Hinweis</p>
                  <p>Der Kunde hat noch <strong>{selectedSubscription?.credits_remaining} Credits</strong> übrig. Diese bleiben erhalten, können aber nach der Kündigung nicht mehr verwendet werden.</p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleCancel}
              disabled={actionLoading}
            >
              {actionLoading ? 'Wird gekündigt...' : 'Abo kündigen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Credits Dialog */}
      <Dialog open={addCreditsDialogOpen} onOpenChange={setAddCreditsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Credits hinzufügen</DialogTitle>
            <DialogDescription>
              Füge zusätzliche Credits zum Abo "{selectedSubscription?.name}" hinzu.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <p className="text-sm text-gray-500 mb-2">
                Aktuell: <strong>{selectedSubscription?.credits_remaining}</strong> / {selectedSubscription?.credits_total} Credits
              </p>
            </div>
            <div>
              <label className="text-sm font-medium">Anzahl Credits</label>
              <Input
                type="number"
                min="1"
                value={creditsToAdd}
                onChange={(e) => setCreditsToAdd(parseInt(e.target.value) || 0)}
                placeholder="z.B. 5"
              />
            </div>
            {creditsToAdd > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-800">
                  Neue Summe: <strong>{(selectedSubscription?.credits_remaining || 0) + creditsToAdd}</strong> Credits
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddCreditsDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleAddCredits}
              disabled={actionLoading || creditsToAdd <= 0}
            >
              {actionLoading ? 'Wird hinzugefügt...' : `${creditsToAdd} Credits hinzufügen`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Renew Dialog */}
      <Dialog open={renewDialogOpen} onOpenChange={setRenewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Abo manuell verlängern</DialogTitle>
            <DialogDescription>
              Verlängerung für "{selectedSubscription?.name}" - {selectedSubscription?.customer_name}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <RefreshCw className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium">Was passiert bei der Verlängerung?</p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Credits werden entsprechend der Abo-Vorlage hinzugefügt</li>
                    <li>Eine neue Rechnung wird erstellt ({formatCurrency(selectedSubscription?.price)})</li>
                    <li>Der Abo-Status wird auf "Aktiv" gesetzt</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenewDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleRenew}
              disabled={actionLoading}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {actionLoading ? 'Wird verlängert...' : 'Jetzt verlängern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerSubscriptionsPage;
